import React from 'react'
import { compose } from 'recompose'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import history from 'browserHistory'
import { FormattedMessage } from 'react-intl'
import classNames from 'classnames'
import Modal from 'react-modal'

import { getSavedPanelWidth } from 'components/Layout/AppLayout/Panel/resizable'
import RoutesHelper from 'helpers/RoutesHelper'
import emptyStateSource from './emptyState.svg'
import memoize from 'memoize-one'
import refreshSearchToken from './dataSync/refreshSearchToken'
import withAlgoliaClient from './dataSync/withAlgoliaClient'
import providesSaveSearchedTerm from './dataSync/providesSaveSearchedTerm'
import { selectors as UISelectors } from 'data/ui'
import {
  getSearchState,
  switchToFullText as switchToFullTextSearch,
  exit as exitSearch,
  clear as clearSearch,
  setQuery as setSearchQuery,
  setHighlightedIndex as setHighlightedEntryIndex,
  setNavigatingInFullTextSearch,
} from 'data/search'

import {
  track,
  trackNavigateFromQuickSwitch,
  trackNavigateFromSearch,
} from 'data/analytics/actions'

import IconButton from 'components/Utils/IconButton'
import { TooltipOnHover } from 'components/Utils/TooltipOnHover'
import { CloseIcon } from 'components/Utils/Icons'
import FullTextSearchResults from './FullText'
import QuickSwitchSearchResults from './QuickSwitch'
import { quickSwitch as clearShorcut } from 'helpers/ShortcutHelpers'

const ENTER = 13
const TAB = 9
const UP = 38
const DOWN = 40

class SearchResults extends React.Component {
  static propTypes = {
    activeNoteId: PropTypes.string,
    query: PropTypes.string.isRequired,
    isActive: PropTypes.bool.isRequired,
    showFullTextResults: PropTypes.bool.isRequired,
    highlightedEntryIndex: PropTypes.number.isRequired,
    switchToFullTextSearch: PropTypes.func.isRequired,
    exitSearch: PropTypes.func.isRequired,
    navigatingFullTextSearch: PropTypes.bool.isRequired,
    setNavigatingInFullTextSearch: PropTypes.func.isRequired,
    saveSearchedTerm: PropTypes.func.isRequired,
    algoliaClient: PropTypes.object.isRequired,
  }

  state = {
    entries: [],
  }

  inputRef = React.createRef()

  // memoize this function so it's only called when opening the search
  saveInitialNote = memoize(isActive => {
    if (isActive) {
      this.initialActiveNoteId = this.props.activeNoteId
    }
  })

  render() {
    this.saveInitialNote(this.props.isActive)
    return (
      <Modal
        isOpen={this.props.isActive}
        onRequestClose={this.props.exitSearch}
        style={{
          content: {
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            width: getSavedPanelWidth(),
            borderRadius: 0,
            padding: 0,
            border: 'none',
            outline: 'none',
          },
          overlay: {
            zIndex: 12,
            width: getSavedPanelWidth(),
          },
        }}
        contentLabel="Search results"
        className="shadow-hovering dark-mode color-mode-elevated"
      >
        <div className="flexbox flexbox-column height-full background-shade-100 shade-05">
          <div
            className="padding-horizontal-medium padding-top-medium flexbox flexbox-justify-end"
            onClick={this.props.exitSearch}
          >
            <IconButton colorScheme="whiteOnDark">
              <CloseIcon size={16} />
            </IconButton>
          </div>
          <div className="flexbox flexbox-align-center padding-horizontal-medium">
            <input
              id="search-panel-input"
              ref={this.inputRef}
              className={classNames(
                'shade-05 placeholder-shade-30',
                'padding-vertical-large',
                'flexbox-grow width-full bold background-transparent border-none  font-size-larger'
              )}
              placeholder="Find anything"
              autoFocus
              value={this.props.query}
              onChange={this.onQueryChange}
              onKeyDown={this.onKeyDown}
              autoComplete="off"
            />
            {
              <TooltipOnHover id="clear-search" tooltipContent={clearShorcut}>
                <div
                  className={classNames(
                    'flexbox-no-grow',
                    'pointer border-none radius-normal',
                    'margin-left-medium padding-horizontal-small padding-vertical-smaller',
                    'shade-10 shade-05-on-hover background-shade-40-on-hover',
                    'font-weight-medium font-size-large ',
                    this.props.query ? 'opacity-full' : 'opacity-null'
                  )}
                  onClick={() => {
                    this.inputRef.current.focus()
                    this.props.clearSearch()
                  }}
                >
                  Clear
                </div>
              </TooltipOnHover>
            }
          </div>

          {this.state.entries.length > 0 &&
            this.props.showFullTextResults && (
              <div className="shade-30 font-size-small margin-horizontal-medium margin-top-large margin-bottom-normal opacity-normal uppercase bold">
                <FormattedMessage
                  id="search.resultsCount"
                  defaultMessage={`{count, plural,
                  one {1 result}
                  other {# results}
                }`}
                  values={{ count: this.state.entries.length }}
                />
              </div>
            )}
          {/* fs-block & data-hj-suppress hides content for FullStory & Hotjar */}
          <div className="scrollable fs-block data-hj-suppress">
            {this.props.showFullTextResults ? (
              <FullTextSearchResults
                query={this.props.query}
                algoliaClient={this.props.algoliaClient}
                onEntrySelected={this.onEntrySelected}
                onEntries={this.onEntries}
                highlightedEntryIndex={this.props.highlightedEntryIndex}
                renderEmptyPlaceholder={this.renderEmptyPlaceholder}
                setHighlightedEntryIndex={this.setHighlightedEntryIndex}
              />
            ) : (
              <QuickSwitchSearchResults
                query={this.props.query}
                algoliaClient={this.props.algoliaClient}
                onEntrySelected={this.onEntrySelected}
                onEntries={this.onEntries}
                highlightedEntryIndex={this.props.highlightedEntryIndex}
                renderEmptyPlaceholder={this.renderEmptyPlaceholder}
                setHighlightedEntryIndex={this.setHighlightedEntryIndex}
              />
            )}
          </div>
        </div>
      </Modal>
    )
  }

  onQueryChange = event => this.props.setSearchQuery(event.target.value)

  onEntries = entries => {
    this.setState({ entries })
  }

  onKeyDown = event => {
    if (event.keyCode === ENTER) {
      event.preventDefault()
      const entry = this.state.entries[this.props.highlightedEntryIndex]
      if (entry) {
        this.onEntrySelected(entry)
        return
      }
    }
    if (event.keyCode === UP || (event.keyCode === TAB && event.shiftKey)) {
      event.preventDefault()
      event.stopPropagation()
      this.setHighlightedEntryIndex(this.props.highlightedEntryIndex - 1)
    }
    if (event.keyCode === DOWN || (event.keyCode === TAB && !event.shiftKey)) {
      event.preventDefault()
      event.stopPropagation()
      this.setHighlightedEntryIndex(this.props.highlightedEntryIndex + 1)
    }
  }

  setHighlightedEntryIndex = index => {
    const newIndex = Math.max(0, Math.min(index, this.state.entries.length - 1))
    this.props.setHighlightedEntryIndex(newIndex)
  }

  onEntrySelected = entry => {
    if (entry.type !== 'search' && !this.props.showFullTextResults) {
      this.props.exitSearch()
    }

    if (this.props.showFullTextResults) {
      this.props.setNavigatingInFullTextSearch(true)
    }

    switch (entry.type) {
      case 'search': {
        this.props.track('OPEN_SEARCH')
        this.props.switchToFullTextSearch({ query: entry.data })
        return
      }
      case 'notes': {
        if (this.props.showFullTextResults) {
          this.props.trackNavigateFromSearch({
            target: 'note',
            source: 'search',
          })
        } else {
          this.props.trackNavigateFromQuickSwitch({
            target: 'note',
            source: this.props.query.length > 0 ? 'search' : 'recent',
          })
        }
        if (this.props.showFullTextResults) {
          this.props.saveSearchedTerm({
            input: {
              term: this.props.query,
            },
          })
        }
        history.push(
          Boolean(entry.data.channelId)
            ? RoutesHelper.noteChannelShow({
                channelId: entry.data.channelId,
                noteId: entry.data.id,
                from: this.initialActiveNoteId,
              })
            : RoutesHelper.noteShow({
                noteId: entry.data.id,
                from: this.initialActiveNoteId,
              })
        )
        return
      }
      case 'channels': {
        this.props.trackNavigateFromQuickSwitch({
          target: 'channel',
          source: this.props.query.length > 0 ? 'search' : 'recent',
        })
        const channelId = entry.data.id
        const route = RoutesHelper.channelShow({ channelId })
        history.push(route)
        break
      }
      default:
    }
  }

  renderEmptyPlaceholder() {
    return (
      <div className="flexbox flexbox-align-center flexbox-column margin-top-huge padding-top-huge">
        <img src={emptyStateSource} alt="no result illustration" />
        <div className="margin-top-largest font-weight-medium">
          We didn’t find any results.
        </div>
      </div>
    )
  }
}

export default compose(
  providesSaveSearchedTerm,
  connect(
    state => {
      const searchState = getSearchState(state)
      return {
        activeNoteId: UISelectors.getActiveNoteId(state),
        isActive: searchState.isActive,
        query: searchState.query,
        showFullTextResults: searchState.isInFullText,
        highlightedEntryIndex: searchState.highlightedIndex,
        navigatingFullTextSearch: searchState.navigatingFullTextSearch,
      }
    },
    {
      clearSearch,
      exitSearch,
      setSearchQuery,
      switchToFullTextSearch,
      setHighlightedEntryIndex,
      setNavigatingInFullTextSearch,
      track,
      trackNavigateFromQuickSwitch,
      trackNavigateFromSearch,
    }
  ),
  refreshSearchToken(({ isActive }) => isActive),
  withAlgoliaClient()
)(SearchResults)
